/**
 * Pixel geometry → calibrated millimetre geometry.
 *
 * This is the one place the two coordinate frames meet, and they disagree about
 * more than scale:
 *
 * | | pixel frame | mm frame |
 * |---|---|---|
 * | origin | top-left of the scan | bottom-left of the scan |
 * | y axis | grows downward | grows upward |
 * | winding | outer ring negative | outer ring counter-clockwise |
 * | precision | integer pixel centres | 0.01 mm |
 *
 * The y flip is `imageHeightPx − y`, and it inverts every shoelace sign for
 * free, which is exactly why `traceShapes` winds its pixel ring "backwards":
 * after the flip the mm ring lands on the canonical convention (CCW outer)
 * that the yield maths assumes.
 */

import { ringArea, ringPerimeter } from './geometry'
import type { Point } from './geometry'
import type { TracedShape } from './shapes'

/** Canonical mm precision for stored geometry: hundredths of a millimetre. */
export const MM_DECIMALS = 2
const MM_SCALE = 10 ** MM_DECIMALS

/**
 * Rounds to 0.01 mm, half away from zero, and normalises `-0` to `0` so two
 * geometrically identical rings never serialise differently.
 */
function roundMm(value: number): number {
  const rounded = Math.round(value * MM_SCALE) / MM_SCALE
  return rounded === 0 ? 0 : rounded
}

export interface ShapeMm {
  /** Outer ring in mm, origin bottom-left, y up, counter-clockwise. */
  outer: [number, number][]
  /**
   * Always empty — the trace stopped producing holes. The key stays because
   * this is the `PartGeom` wire shape the API expects.
   */
  holes: [number, number][][]
  /** Outer-ring area in mm², by shoelace on the rounded ring. */
  netMm2: number
  /** Boundary length in mm of the closed outer ring. */
  perimMm: number
}

/**
 * Converts a traced shape into calibrated mm geometry.
 *
 * Scale is per axis because a flatbed's carriage step and its sensor pitch are
 * calibrated separately and really do differ by a fraction of a percent; a
 * single "dpi" would bake that error into every part.
 *
 * `netMm2` and `perimMm` are measured on the **rounded** ring, not on the
 * full-precision intermediates, so the reported area is always exactly the area
 * of the polygon the caller was handed — recomputing it downstream from
 * `outer` reproduces this number bit for bit. It inherits the pixel-centre
 * under-estimate documented on {@link TracedShape.areaPx}.
 *
 * @throws RangeError when a scale is not a finite positive number, or when
 *   `imageHeightPx` is not finite.
 */
export function pxToMm(
  shape: TracedShape,
  pxPerMmX: number,
  pxPerMmY: number,
  imageHeightPx: number,
): ShapeMm {
  if (!Number.isFinite(pxPerMmX) || pxPerMmX <= 0) {
    throw new RangeError(`pxToMm: pxPerMmX must be a positive number, got ${pxPerMmX}`)
  }
  if (!Number.isFinite(pxPerMmY) || pxPerMmY <= 0) {
    throw new RangeError(`pxToMm: pxPerMmY must be a positive number, got ${pxPerMmY}`)
  }
  if (!Number.isFinite(imageHeightPx)) {
    throw new RangeError(`pxToMm: imageHeightPx must be finite, got ${imageHeightPx}`)
  }

  const convert = (ring: readonly Point[]): Point[] =>
    ring.map(([x, y]): Point => [roundMm(x / pxPerMmX), roundMm((imageHeightPx - y) / pxPerMmY)])

  const outer = convert(shape.outer)

  return { outer, holes: [], netMm2: ringArea(outer), perimMm: ringPerimeter(outer) }
}
